import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { IntegrationService } from '../services/integrationsService';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';

interface IntegrationsExamplesProps {
  maxCount?: number;
  onAddClick?: () => void;
}

export const IntegrationsExamples: React.FC<IntegrationsExamplesProps> = ({ maxCount = 12, onAddClick }) => {
  const { t } = useTranslation();
  const { data: integrations, isLoading } = useQuery({
    queryKey: ['integration-examples'],
    queryFn: () => IntegrationService.getAll(),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center mt-6 mb-8">
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (!integrations || integrations.length === 0) {
    return null;
  }

  // Pegar os primeiros templates com logos válidas  
  const exampleIntegrations = integrations
    .filter(integration => {
      const hasValidLogo = integration.logo_url && 
                          integration.logo_url.trim() !== '' &&
                          !integration.logo_url.includes('undefined') &&
                          !integration.logo_url.includes('null');
      return hasValidLogo;
    })
    .slice(0, maxCount);

  if (exampleIntegrations.length === 0) {
    return null;
  }

  return (
    <TooltipProvider>
      <div className="flex items-center justify-center mt-6 mb-8">
        <div className="flex -space-x-4">
          {exampleIntegrations.map((integration, index) => (
            <Tooltip key={integration.id}>
              <TooltipTrigger asChild>
                <div
                  className="relative cursor-pointer"
                  style={{ zIndex: exampleIntegrations.length - index + 1 }}
                >
                  <div className="w-14 h-14 rounded-full bg-card border-3 border-border shadow-lg overflow-hidden hover:scale-110 transition-transform duration-200">
                    <img
                      src={integration.logo_url ?? undefined}
                      alt={integration.label || integration.name || 'Integration logo'}
                      className="w-full h-full object-contain p-2"
                      onError={(e) => {
                        // Se a imagem falhar, usar um placeholder
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-sm font-medium">{integration.label || integration.name}</p>
              </TooltipContent>
            </Tooltip>
          ))}
          {/* Logo de reticências indicando mais integrações */}
          <div
            className="relative cursor-pointer"
            style={{ zIndex: 0 }}
            onClick={onAddClick}
          >
            <div className="w-14 h-14 rounded-full bg-muted/50 border-3 border-border shadow-lg flex items-center justify-center hover:scale-110 hover:bg-primary/10 transition-all duration-200">
              <span className="text-muted-foreground font-bold text-lg">...</span>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default IntegrationsExamples;
